import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

import Select from '../../../components/ui/Select';
import { cn } from '../../../utils/cn';

const TaskManagementPanel = ({ compact = false }) => {
  const [showNewTaskModal, setShowNewTaskModal] = useState(false);
  const [filterPriority, setFilterPriority] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');

  const [tasks, setTasks] = useState([
    {
      id: 1,
      title: 'Review lab results and adjust medication',
      description: 'Critical potassium levels require immediate attention',
      assignedTo: 'Dr. Michael Chen',
      assignedBy: 'Dr. Sarah Williams',
      priority: 'urgent',
      status: 'in-progress',
      dueDate: '2026-01-14',
      patient: 'Sarah Johnson',
      department: 'Cardiology',
      createdAt: '2026-01-14 08:30'
    },
    {
      id: 2,
      title: 'Complete discharge planning documentation',
      description: 'Prepare discharge summary and follow-up instructions',
      assignedTo: 'Nurse Emily Roberts',
      assignedBy: 'Dr. Michael Chen',
      priority: 'high',
      status: 'pending',
      dueDate: '2026-01-15',
      patient: 'Sarah Johnson',
      department: 'ICU',
      createdAt: '2026-01-14 09:15'
    },
    {
      id: 3,
      title: 'Schedule follow-up cardiology appointment',
      description: 'Patient needs 2-week post-discharge follow-up',
      assignedTo: 'Admin Staff',
      assignedBy: 'Dr. Michael Chen',
      priority: 'normal',
      status: 'completed',
      dueDate: '2026-01-14',
      patient: 'Sarah Johnson',
      department: 'Scheduling',
      createdAt: '2026-01-13 14:20'
    },
    {
      id: 4,
      title: 'Coordinate physical therapy evaluation',
      description: 'Assess mobility and create rehabilitation plan',
      assignedTo: 'John Martinez',
      assignedBy: 'Nurse Emily Roberts',
      priority: 'high',
      status: 'in-progress',
      dueDate: '2026-01-14',
      patient: 'Sarah Johnson',
      department: 'Rehabilitation',
      createdAt: '2026-01-14 07:45'
    }
  ]);

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'urgent':
        return 'bg-error/10 text-error border-error';
      case 'high':
        return 'bg-warning/10 text-warning border-warning';
      case 'normal':
        return 'bg-primary/10 text-primary border-primary';
      case 'low':
        return 'bg-muted text-muted-foreground border-border';
      default:
        return 'bg-muted text-muted-foreground border-border';
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed':
        return 'bg-success/10 text-success border-success';
      case 'in-progress':
        return 'bg-primary/10 text-primary border-primary';
      case 'pending':
        return 'bg-warning/10 text-warning border-warning';
      case 'overdue':
        return 'bg-error/10 text-error border-error';
      default:
        return 'bg-muted text-muted-foreground border-border';
    }
  };

  const filteredTasks = tasks?.filter(task => {
    if (filterPriority !== 'all' && task?.priority !== filterPriority) return false;
    if (filterStatus !== 'all' && task?.status !== filterStatus) return false;
    return true;
  });

  const displayTasks = compact ? filteredTasks?.slice(0, 3) : filteredTasks;

  return (
    <div className="bg-card rounded-lg border border-border shadow-elevation-1">
      <div className="p-4 border-b border-border">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          <h2 className="text-lg font-semibold text-foreground flex items-center gap-2">
            <Icon name="CheckSquare" size={20} className="text-primary" />
            Task Management
          </h2>
          <div className="flex items-center gap-2">
            {!compact && (
              <>
                <Select
                  value={filterPriority}
                  onChange={setFilterPriority}
                  options={[
                    { value: 'all', label: 'All Priorities' },
                    { value: 'urgent', label: 'Urgent' },
                    { value: 'high', label: 'High' },
                    { value: 'normal', label: 'Normal' },
                    { value: 'low', label: 'Low' }
                  ]}
                  className="w-40"
                />
                <Select
                  value={filterStatus}
                  onChange={setFilterStatus}
                  options={[
                    { value: 'all', label: 'All Status' },
                    { value: 'pending', label: 'Pending' },
                    { value: 'in-progress', label: 'In Progress' },
                    { value: 'completed', label: 'Completed' }
                  ]}
                  className="w-40"
                />
              </>
            )}
            <Button variant="default" size="sm" iconName="Plus" onClick={() => setShowNewTaskModal(true)}>
              New Task
            </Button>
          </div>
        </div>
      </div>

      <div className="p-4 space-y-3">
        {displayTasks?.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground">
            <Icon name="CheckSquare" size={48} className="mx-auto mb-2 opacity-50" />
            <p>No tasks found</p>
          </div>
        ) : (
          displayTasks?.map((task) => (
            <div
              key={task?.id}
              className="bg-background rounded-lg border border-border p-4 hover:shadow-elevation-2 transition-shadow"
            >
              <div className="flex items-start justify-between gap-3 mb-3">
                <div className="flex-1">
                  <h3 className="font-semibold text-foreground mb-1">{task?.title}</h3>
                  <p className="text-sm text-muted-foreground">{task?.description}</p>
                </div>
                <div className="flex items-center gap-2">
                  <span className={cn(
                    "inline-flex items-center px-2 py-1 rounded-md text-xs font-medium border",
                    getPriorityColor(task?.priority)
                  )}>
                    {task?.priority?.toUpperCase()}
                  </span>
                  <span className={cn(
                    "inline-flex items-center px-2 py-1 rounded-md text-xs font-medium border",
                    getStatusColor(task?.status)
                  )}>
                    {task?.status?.replace('-', ' ')?.toUpperCase()}
                  </span>
                </div>
              </div>

              <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-3 text-sm">
                <div>
                  <p className="text-muted-foreground text-xs mb-1">Assigned To</p>
                  <p className="font-medium text-foreground">{task?.assignedTo}</p>
                </div>
                <div>
                  <p className="text-muted-foreground text-xs mb-1">Patient</p>
                  <p className="font-medium text-foreground">{task?.patient}</p>
                </div>
                <div>
                  <p className="text-muted-foreground text-xs mb-1">Department</p>
                  <p className="font-medium text-foreground">{task?.department}</p>
                </div>
                <div>
                  <p className="text-muted-foreground text-xs mb-1">Due Date</p>
                  <p className="font-medium text-foreground">{task?.dueDate}</p>
                </div>
              </div>

              <div className="flex items-center justify-between pt-3 border-t border-border">
                <span className="text-xs text-muted-foreground">
                  Created by {task?.assignedBy} • {task?.createdAt}
                </span>
                <div className="flex items-center gap-2">
                  <Button variant="ghost" size="xs" iconName="MessageSquare">
                    Comment
                  </Button>
                  <Button variant="ghost" size="xs" iconName="Edit">
                    Edit
                  </Button>
                  {task?.status !== 'completed' && (
                    <Button variant="success" size="xs" iconName="Check">
                      Complete
                    </Button>
                  )}
                </div>
              </div>
            </div>
          ))
        )}

        {compact && filteredTasks?.length > 3 && (
          <Button variant="outline" fullWidth iconName="ArrowRight" iconPosition="right">
            View All {filteredTasks?.length} Tasks
          </Button>
        )}
      </div>
    </div>
  );
};

export default TaskManagementPanel;